/* -----------------------------
    SLIDER COMPONENT
   ----------------------------- */
class BaseSlider extends HTMLElement {
  constructor() {
    super();

    this.defaultOptions = {
      slidesPerView: "auto",
      spaceBetween: 0,
      navigation: false,
      pagination: false,
      scrollbar: false,
      grabCursor: true,
      on: {
        afterInit: () => {
          this.classList.add(this.modifiers.initialized);
        }
      }
    };

    this.swiperInstance = null;

    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onSectionUnload = this.onSectionUnload.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
  }

  connectedCallback() {
    this.addEventListener("slider:init", this.initSlider.bind(this));
    this.addEventListener("slider:update", this.updateSlider.bind(this));
    this.addEventListener("slider:destroy", this.destroySlider.bind(this));

    this.initSlider();
    this.stopPropagationForScrollableChildren();

    if (this.shouldHandleEditorEvents) {
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  disconnectedCallback() {
    this.removeEventListener("slider:init", this.initSlider.bind(this));
    this.removeEventListener("slider:update", this.updateSlider.bind(this));
    this.removeEventListener("slider:destroy", this.destroySlider.bind(this));

    this.destroySlider();

    if (this.shouldHandleEditorEvents) {
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  get selectors() {
    return {
      wrapper: ".swiper-wrapper",
      slide: "slider-slide"
    }
  }

  get modifiers() {
    return {
      initialized: "initialized"
    }
  }

  get attributes() {
    return {
      pagination: "pagination",
      scrollbar: "scrollbar",
      prevButton: "prev-button",
      nextButton: "next-button",
      freeMode: "freemode",
      fade: "fade",
      centerInsufficientSlides: "center-insufficient-slides",
      initialSlide: "initial-slide",
      vertical: "vertical",
      loop: "loop",
      fraction: "fraction",
      autoHeight: "auto-height",
      shopifyDesignMode: "shopify-design-mode"
    }
  }

  get options() {
    const slideCount = this.slides.length;

    const
      paginationSelector = this.getAttribute(this.attributes.pagination),
      paginationElement = paginationSelector ? this.querySelector(paginationSelector) : null;

    const
      fractionSelector = this.getAttribute(this.attributes.fraction),
      fractionElement = fractionSelector ? this.querySelector(fractionSelector) : null;

    const
      scrollbarSelector = this.getAttribute(this.attributes.scrollbar),
      scrollbarElement = scrollbarSelector ? this.querySelector(scrollbarSelector) : null;

    const
      prevButtonSelector = this.getAttribute(this.attributes.prevButton),
      prevButtonElement = prevButtonSelector ? this.querySelector(prevButtonSelector) ?? document.querySelector(prevButtonSelector)  : null;

    const
      nextButtonSelector = this.getAttribute(this.attributes.nextButton),
      nextButtonElement = nextButtonSelector ? this.querySelector(nextButtonSelector) ?? document.querySelector(nextButtonSelector) : null;

    const freeMode = this.getAttribute(this.attributes.freeMode) === "true";
    const fade = this.getAttribute(this.attributes.fade) === "true";
    const centerInsufficientSlides = this.getAttribute(this.attributes.centerInsufficientSlides) === "true";
    const initialSlide = this.getAttribute(this.attributes.initialSlide) ?? 0;
    const vertical = this.getAttribute(this.attributes.vertical) === "true";
    const autoHeight = this.getAttribute(this.attributes.autoHeight) === "true";

    const loopRequested = this.getAttribute(this.attributes.loop) === "true";
    const loop = loopRequested && slideCount > 2;
    const rewind = loopRequested && slideCount === 2;

    const customOptions = {
      ...(fractionElement ? {
        pagination: {
          el: fractionElement,
          type: "fraction"
        }
      } : paginationElement ? {
        pagination: {
          el: paginationElement,
          clickable: true
        }
      } : {}),
      ...(scrollbarElement && {
        scrollbar: {
          el: scrollbarElement,
          draggable: false
        }
      }),
      ...(prevButtonElement && nextButtonElement && {
        navigation: {
          prevEl: prevButtonElement,
          nextEl: nextButtonElement
        }
      }),
      ...(freeMode && {
        freeMode: true
      }),
      ...(fade && {
        effect: "fade",
        fadeEffect: {
          crossFade: true
        }
      }),
      ...(centerInsufficientSlides && {
        centerInsufficientSlides: true
      }),
      ...(initialSlide && {
        initialSlide
      }),
      ...(vertical && {
        direction: "vertical",
        slidesPerView: 1,
        speed: 800
      }),
      ...(loop && {
        loop: true
      }),
      ...(rewind && {
        rewind: true
      }),
      ...(autoHeight && {
        autoHeight: true
      }),
    };

    return { ...this.defaultOptions, ...customOptions };
  }

  get slides() {
    return [...this.querySelectorAll(this.selectors.slide)];
  }

  set options(options) {
    this._options = { ...this._options, ...options };
  }

  get shouldHandleEditorEvents() {
    return this.hasAttribute(this.attributes.shopifyDesignMode);
  }

  get sectionId() {
    return this.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
  }

  get sessionStorageKey() {
    const sectionId = this.sectionId;
    const sliderIndex = [...document.querySelectorAll(`#shopify-section-${sectionId} ${this.tagName.toLowerCase()}`)].indexOf(this);

    return `slider-active-index-${sectionId}-${sliderIndex}`;
  }

  isOwnSection(event) {
    return event.detail.sectionId === this.sectionId;
  }

  onSectionLoad(event) {
    if (!this.isOwnSection(event)) return;

    const storedIndex = sessionStorage.getItem(this.sessionStorageKey);

    if (storedIndex !== null && this.swiperInstance) {
      this.swiperInstance.slideTo(parseInt(storedIndex), 0);
    }
  }

  onSectionUnload(event) {
    if (!this.isOwnSection(event)) return;

    if (this.swiperInstance) {
      sessionStorage.setItem(this.sessionStorageKey, this.swiperInstance.activeIndex);
    }
  }

  onBlockSelect(event) {
    if (!this.isOwnSection(event)) return;

    const slide = this.querySelector(`slider-slide[data-block-id="${event.detail.blockId}"]`);

    if (!slide || !this.swiperInstance) return;

    const slideIndex = this.slides.indexOf(slide);

    if (slideIndex === -1) return;

    this.swiperInstance.slideTo(slideIndex);
    sessionStorage.setItem(this.sessionStorageKey, slideIndex);
  }

  onBlockDeselect(event) {
    if (!this.isOwnSection(event)) return;
  }

  initSlider() {
    if (this.swiperInstance) return;

    this.swiperInstance = new window.Swiper(this, this.options);
  }

  destroySlider(deleteInstance = true, clearStyles = true) {
    if (!this.swiperInstance) return;

    this.swiperInstance.destroy(deleteInstance, clearStyles);
    this.swiperInstance = null;
  }

  updateSlider() {
    if (!this.swiperInstance) return;

    this.swiperInstance.update();
  }

  stopPropagationForScrollableChildren() {
    const scrollableChildren = [...this.querySelectorAll(".swiper-no-swiping")];

    if (!scrollableChildren.length) return;

    scrollableChildren.forEach((scrollableChild) => {
      scrollableChild.addEventListener("touchmove", function (event) {
        event.stopPropagation();
      });
    });
  }
}

if (!customElements.get("base-slider")) {
  customElements.define("base-slider", BaseSlider);
}

class ScrollSlider extends BaseSlider {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(this.initSlider.bind(this));
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();
  }

  get modifiers() {
    return {
      ...super.modifiers,
      bleed: "slider--bleed",
      scroll: "slider--scroll"
    }
  }

  get breakpoints() {
    return {
      desktop: 992,
      mobile: 375
    }
  }

  get wrapper() {
    return this.querySelector(this.selectors.wrapper);
  }

  initSlider() {
    if (window.innerWidth < this.breakpoints.desktop) {
      this.classList.remove(this.modifiers.bleed);
      this.classList.add(this.modifiers.scroll);

      if (this.swiperInstance) {
        this.destroySlider();
      }
    } else {
      this.classList.add(this.modifiers.bleed);
      this.classList.remove(this.modifiers.scroll);

      if (!this.swiperInstance) {
        this.swiperInstance = new window.Swiper(this, this.options);
      }
    }
  }
}

if (!customElements.get("scroll-slider")) {
  customElements.define("scroll-slider", ScrollSlider);
}

class ProductOptionSlider extends ScrollSlider {
  constructor() {
    super();
  }

  get selectors() {
    return {
      ...super.selectors,
      slide: "[data-slide]",
      initialSlide: "[data-initial-slide]"
    }
  }

  get initialSlide() {
    return this.querySelector(this.selectors.initialSlide);
  }

  get options() {
    const initialSlideIndex = this.initialSlide ? this.slides.indexOf(this.initialSlide) : null;

    return {
      ...super.options,
      slidesPerGroup: 3,
      ...(initialSlideIndex && initialSlideIndex > -1 && {
        initialSlide: initialSlideIndex
      })
    }
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  initSlider() {
    super.initSlider();

    if (this.initialSlide) {
      this.wrapper.scrollLeft = this.initialSlide.offsetLeft - parseInt(
        getComputedStyle(document.documentElement).getPropertyValue("--container-padding")
      );
    }
  }
}

if (!customElements.get("product-option-slider")) {
  customElements.define("product-option-slider", ProductOptionSlider);
}

class FilterableSlider extends BaseSlider {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("slider:filter", this.filterSlider.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("slider:filter", this.filterSlider.bind(this));
  }

  get modifiers() {
    return {
      ...super.modifiers,
      hidden: "hidden"
    }
  }

  get attributes() {
    return {
      ...super.attributes,
      filters: "filters"
    }
  }

  get resetFilterValue() {
    return "reset";
  }

  filterSlider(event) {
    const { filterValue } = event?.detail;

    if (!filterValue) {
      return;
    }

    const [slidesToShow, slidesToHide] = this.filterSlidesByValue(filterValue);

    slidesToHide.forEach((item) => {
      item.classList.add(this.modifiers.hidden);
    });

    slidesToShow.forEach((item) => {
      item.classList.remove(this.modifiers.hidden);
    });

    this.updateSlider();
  }

  filterSlidesByValue(value = this.resetFilterValue) {
    const items = this.slides;

    if (value === this.resetFilterValue) {
      return [[...items], []];
    }

    let [itemsToShow, itemsToHide] = [...items].reduce(
      ([itemsToShow, itemsToHide], item) => {
        const filters = item.getAttribute(this.attributes.filters);

        if (!filters) {
          return [[...itemsToShow], [...itemsToHide, item]];
        }

        return filters?.includes(value)
          ? [[...itemsToShow, item], [...itemsToHide]]
          : [[...itemsToShow], [...itemsToHide, item]];
      },
      [[], []]
    );

    if (!itemsToShow.length) {
      itemsToShow = [...items];
    }

    return [itemsToShow, itemsToHide];
  }
}

if (!customElements.get("filterable-slider")) {
  customElements.define("filterable-slider", FilterableSlider);
}

class EmbeddedControlsSlider extends BaseSlider {
  get selectors() {
    return {
      ...super.selectors,
      button: {
        prev: "slider-button[type='prev']",
        next: "slider-button[type='next']"
      },
      pagination: {
        source: "[data-slider-pagination-source]",
        target: "[data-slider-pagination-clone]"
      }
    };
  }

  get modifiers() {
    return {
      ...super.modifiers,
      button: {
        disabled: "swiper-button-disabled"
      }
    };
  }

  initSlider() {
    super.initSlider();

    this.setupNavigation();
    this.setupPagination();
  }

  setupNavigation() {
    this.navigationButtons = {
      prev: [...this.querySelectorAll(this.selectors.button.prev)],
      next: [...this.querySelectorAll(this.selectors.button.next)],
    };

    this.navigationButtons.prev.forEach((button) => {
      button.addEventListener("click", () => this.onNavigationClick("prev"));
    });

    this.navigationButtons.next.forEach((button) => {
      button.addEventListener("click", () => this.onNavigationClick("next"));
    });

    this.updateNavigationState();
    this.swiperInstance.on("slideChange", () => this.updateNavigationState());
  }

  onNavigationClick(direction) {
    if (!this.swiperInstance) return;

    direction === "prev" ? this.swiperInstance.slidePrev() : this.swiperInstance.slideNext();
    this.updateNavigationState();
  }

  updateNavigationState() {
    if (!this.swiperInstance) return;

    const isLooping = this.getAttribute(this.attributes.loop) === "true";

    const isAtFirstSlide = !isLooping && this.swiperInstance.activeIndex === 0;
    const isAtLastSlide = !isLooping && this.swiperInstance.activeIndex === this.slides.length - 1;

    this.navigationButtons.prev.forEach((button) => {
      button.setAttribute("aria-disabled", isAtFirstSlide.toString());
      button.classList.toggle(this.modifiers.button.disabled, isAtFirstSlide);
    });

    this.navigationButtons.next.forEach((button) => {
      button.setAttribute("aria-disabled", isAtLastSlide.toString());
      button.classList.toggle(this.modifiers.button.disabled, isAtLastSlide);
    });
  }

  setupPagination() {
    this.paginationSource = this.querySelector(this.selectors.pagination.source);
    this.paginationTargets = [...this.querySelectorAll(this.selectors.pagination.target)];

    if (!this.paginationSource || !this.paginationTargets.length) return;

    this.syncPaginationTargets();
    this.swiperInstance.on("paginationUpdate", () => this.syncPaginationTargets());
  }

  syncPaginationTargets() {
    this.paginationTargets.forEach((target) => {
      target.innerHTML = this.paginationSource.innerHTML;
    });
  }
}

if (!customElements.get("embedded-controls-slider")) {
  customElements.define("embedded-controls-slider", EmbeddedControlsSlider);
}

class PromotionDealsSlider extends EmbeddedControlsSlider {
  constructor() {
    super();
  }

  initSlider() {
    super.initSlider();

    document.addEventListener("countdown:init", () => {
      const timer = this.querySelector("countdown-timer[state='current']");

      if (!timer) {
        return;
      }

      const slide = timer.closest(this.selectors.slide);

      if (!slide) {
        return;
      }

      this.swiperInstance.slideTo(slide.getAttribute("index"));
      this.updateButtons();
    });
  }
}

if (!customElements.get("promotional-deals-slider")) {
  customElements.define("promotional-deals-slider", PromotionDealsSlider);
}
